import { LitElement, html } from "lit";

import { customElement, property, state } from "lit/decorators.js";

import { AppContextController, appContext } from "ui-common/appContext.ts";
import { type MenuButtonLabels, type MenuItem } from "ui-components/menu-button";
import { DataQueryController } from "ui-common/data-query-controller";
import type { GetNotificationsResult } from "api-contracts/rpc/notifications";
import { notificationsQueryOptions } from "ui-common/queries/notifications";

import { styles } from "./page-header.css";

import { navigateTo } from "../../router";
import { t, translate } from "../../translations/i18n";

import "ui-components/notification-bell";
import "ui-components/notification-modal/notification-modal";

import "../app-logo/app-logo";
import "../router-link/router-link";
import "../user-avatar/user-avatar";
import "@phosphor-icons/webcomponents/PhMagnifyingGlass";

@customElement("page-header")
export class PageHeader extends LitElement {
  static override styles = styles;

  @property()
  override title: string = "";

  @state()
  private hasBackground = false;

  @state()
  private notificationModalOpen = false;

  private appContextController = new AppContextController(this);

  private notificationsController = new DataQueryController<GetNotificationsResult | null>(
    this,
    () => notificationsQueryOptions,
  );

  private handleSlotChange = (e: Event) => {
    const slot = e.target as HTMLSlotElement;
    const assignedNodes = slot.assignedNodes();
    this.hasBackground = assignedNodes.length > 0;
  };

  private get menuItems(): MenuItem[] {
    let menuItems: MenuItem[] = [];
    if (this.appContextController.appContext.currentUser?.id) {
      menuItems = [
        {
          id: "sign-in",
          href: "onboard-creator?step=1",
          label: t("menu.becomeCreator"),
          icon: html`<ph-crown weight="bold" size="20"></ph-crown>`,
        },
        {
          id: "settings",
          href: "settings",
          label: "Support",
          icon: html`<ph-question weight="bold" size="20"></ph-question>`,
        },
        {
          id: "explore",
          href: "/explore",
          label: "Explore",
          icon: html`<ph-magnifying-glass weight="bold" size="20"></ph-magnifying-glass>`,
        },
      ];
    }

    if (this.appContextController.appContext.currentUser?.handle) {
      menuItems = [
        {
          id: "profile",
          href: "profile",
          icon: html`<ph-user weight="bold" size="24"></ph-user>`,
          label: t("menu.editProfile"),
        },
        {
          id: "analytics",
          href: "analytics",
          icon: html`<ph-chart-bar weight="bold" size="24"></ph-chart-bar>`,
          label: t("menu.analytics"),
        },
        {
          id: "settings",
          href: "settings",
          icon: html`<ph-gear weight="bold" size="24"></ph-gear>`,
          label: t("menu.support"),
        },
      ];

      if (appContext.currentUser?.roles.includes("admin")) {
        menuItems.splice(0, 0, {
          id: "admin",
          href: "/admin",
          icon: html`<ph-beer-stein weight="bold" size="24"></ph-beer-stein>`,
          label: t("menu.admin"),
        });
      }

      return menuItems;
    }
    return menuItems;
  }

  private get menuLabels(): MenuButtonLabels {
    return {
      openMenu: t("accessibility.openMenu"),
      viewProfile: t("user.viewProfile"),
      signIn: t("sign-in.signIn"),
      selectEnglish: t("language.selectEnglish"),
      selectDutch: t("language.selectDutch"),
      lightTheme: t("theme.lightTheme"),
      darkTheme: t("theme.darkTheme"),
      manage: t("menu.manage"),
      signOut: t("menu.signOut"),
      becomeCreator: t("menu.becomeCreator"),
    };
  }

  private handleMenuNavigate = (event: CustomEvent) => {
    const { href } = event.detail;

    if (href === "/admin" && appContext.currentUser?.roles.includes("admin")) {
      window.open(href, "_self", "noopener,noreferrer");
      return;
    }

    if (href === "/explore" && appContext.currentUser?.id) {
      return window.open(href, "_self", "noopener,noreferrer");
    }

    navigateTo(href);
  };

  private handleViewProfile = () => {
    if (!appContext.currentUser?.handle) return;

    window.open(`/${appContext.currentUser.handle}`, "_self", "noopener,noreferrer");
  };

  private handleSignOut = () => {
    this.appContextController.appContext.signOut();
  };

  private handleNotificationBellClick = () => {
    this.notificationModalOpen = true;
  };

  private handleNotificationModalClose = () => {
    this.notificationModalOpen = false;
  };

  override render() {
    const user = this.appContextController.appContext.currentUser;
    const memberSinceYear = user
      ? translate("user.memberSince", {
          year: user?.memberSinceDate ? new Date(user.memberSinceDate).getFullYear().toString() : "unknown",
        })
      : "";

    const notificationsData = this.notificationsController?.result?.data as GetNotificationsResult | null | undefined;
    const notifications = notificationsData?.notifications || [];

    return html`
      <div class="header-background">
        <slot name="background" @slotchange=${this.handleSlotChange}></slot>
      </div>
      <header class="${this.hasBackground ? "with-background" : ""}">
        <div class="logo-title-container">
          <div class="logo-container">
            <app-logo class="show-on-mobile"></app-logo>
          </div>
          ${this.title !== "" ? html`<h1 class="page-title">${this.title}</h1>` : html`<slot name="title"></slot>`}
        </div>
        <div class="buttons-wrapper">
          <div class="menu-controls">
            <notification-bell @click=${this.handleNotificationBellClick}></notification-bell>
            <menu-button
              .userName=${user?.name || user?.handle || ""}
              .userHandle=${user?.handle}
              .memberSinceYear=${memberSinceYear}
              .userAvatar=${user?.avatarUrl}
              .avatarComponent=${html`<user-avatar size="large"></user-avatar>`}
              .menuItems=${this.menuItems}
              .userId=${user?.id}
              .labels=${this.menuLabels}
              @sign-out=${this.handleSignOut}
              @view-profile=${this.handleViewProfile}
              @menu-navigate=${this.handleMenuNavigate}
            ></menu-button>
          </div>
          <div class="action-buttons">
            <slot></slot>
          </div>
        </div>
      </header>
      <notification-modal
        .open=${this.notificationModalOpen}
        .notifications=${notifications}
        @close=${this.handleNotificationModalClose}
      ></notification-modal>
    `;
  }
}
